<template>
  <section>
    <DataSlider
      type="year"
      :items="monthItems"
      :model-value="selectedMonth"
      :border-bottom="true"
      @change="onMonthChange"
    />

    <QueryErrorNotice :error="calendarPageErrors" />

    <div class="week week__card mt-2">
      <div class="week__card__body">
        <div class="month" v-if="monthGrid.weeks.length">
          <div class="week">
            <div class="day text-dark">{{ t("mon_short_text") }}</div>
            <div class="day text-dark">{{ t("tue_short_text") }}</div>
            <div class="day text-dark">{{ t("wed_short_text") }}</div>
            <div class="day text-dark">{{ t("thu_short_text") }}</div>
            <div class="day text-dark">{{ t("fri_short_text") }}</div>
            <div class="day text-dark text-pink">{{ t("sat_short_text") }}</div>
            <div class="day text-dark text-pink">{{ t("sun_short_text") }}</div>
          </div>
          <div
            class="week"
            v-for="(week, weekIndex) in monthGrid.weeks"
            :key="`week-${weekIndex}`"
          >
            <div
              v-for="(day, dayIndex) in week.days"
              :key="`day-${weekIndex}-${dayIndex}`"
              class="day"
              :class="getDayType(day)"
            >
              <span
                class="circle work text-center"
                :class="day.class"
                @click="!day.class.out && day.class.work && navigate(day)"
              >
                <span :class="{ starDay: day.class.test }">{{ day.day }}</span>
                <i v-if="day.class.test" class="fa fa-star text-pink"></i>
              </span>
            </div>
          </div>
        </div>

        <div class="plans">
          <div class="infoCalendar">
            <div
              v-for="(item, index) in infoLegend"
              :key="`info-${index}`"
              class="infoList"
            >
              <div v-if="item !== 'holidayInfo'" :class="item" class="circuleFont">
                <span class="material-icons circule">lens</span>
              </div>
              <i v-else class="fa fa-star text-pink starIcon"></i>
              <span>{{ t(item) }}</span>
            </div>
          </div>

          <div class="holidays" v-if="holidayRangeText">
            <div class="holidaysTitle">
              <span>{{ t("holidayTitle") }}</span>
            </div>
            <div class="holidaysInfo">
              <span>{{ t("holidayText") }} {{ holidayRangeText }}</span>
            </div>
          </div>

          <div class="plan" v-for="(plan, index) in groupedPlans" :key="`plan-${index}`">
            <div class="date" @click="navigateMaterial(plan)">
              <div>
                <span>{{ formatPlanDate(plan.date) }}</span>
              </div>
              <span class="icon">
                <i class="fas fa-chevron-right"></i>
              </span>
            </div>

            <div class="planBody">
              <div class="left__line"></div>
              <div
                class="info d-flex"
                v-for="(subject, subjectIndex) in plan.subjects"
                :key="`subject-${subjectIndex}`"
              >
                <div class="ml-2 lens__head">
                  <span class="material-icons lens__body f-14">lens</span>
                </div>

                <div class="d-flex subject">
                  <div class="subject__name">{{ subject.name }}</div>
                  <div>
                    <div
                      v-for="(planItem, planItemIndex) in subject.item"
                      :key="`plan-item-${planItemIndex}`"
                    >
                      <span class="theme__name">{{ planItem.theme_name }}</span>
                      <div>
                        <div class="bottom__line"></div>
                        <span class="material-icons task__lens">panorama_fish_eye</span>

                        <div
                          v-for="(task, taskIndex) in getTasks(planItem)"
                          :key="`task-${taskIndex}`"
                        >
                          <div v-if="task.end_date" class="task__head">
                            <span>
                              <span class="trTask f-14">{{ t("theTasks") }}:</span>
                              <span class="task__name f-14">{{
                                task?.sdot_data?.name || task?.name || ""
                              }}</span>
                            </span>
                            <div class="end__date">
                              <div class="d-flex align-items-center">
                                <template v-if="getProgressInfo(task).kind === 'timer'">
                                  {{ getProgressInfo(task).text }}
                                  <span class="ml-2">{{
                                    getProgressInfo(task).time
                                  }}</span>
                                </template>
                                <template
                                  v-else-if="getProgressInfo(task).kind === 'date'"
                                >
                                  {{ getProgressInfo(task).text }}
                                  {{ getProgressInfo(task).date }}
                                </template>
                                <template v-else>
                                  {{ getProgressInfo(task).text }}
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!groupedPlans.length">
            <span class="emptyCalendarPlans">{{ t("emptyCalendarPlans") }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import DataSlider from "@/components/common/DataSlider.vue";
import QueryErrorNotice from "@/components/common/QueryErrorNotice.vue";
import {
  useCalendarHolidaysQuery,
  useCalendarMarksQuery,
  useCalendarPlansQuery,
  useCalendarSubjectsByIdsQuery,
  usePeriodsQuery,
} from "@/queries";
import { useLoadingStore } from "@/stores/loading";
import { useTopbarStore } from "@/stores/topbar";
import { useViewerStore } from "@/stores/viewer";
import {
  parsePeriodsLegacy,
  toPeriodDateKey,
  toUtcDaySeconds,
} from "@/utils/periodSlider";

const { t } = useI18n();
const router = useRouter();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
const viewerStore = useViewerStore();
viewerStore.initFromStorage();

const selectedMonth = ref(null);
const nowTs = ref(Date.now());
const infoLegend = ["todayInfo", "examCalendar", "holidayInfo", "dayoff"];

let nowTimer = null;

const selectedChild = computed(() => viewerStore.selectedChild || null);
const groupId = computed(() =>
  Number(selectedChild.value?.group_id || viewerStore.profile?.group_id || 0)
);
const pupilId = computed(() =>
  Number(selectedChild.value?.pupil_id || viewerStore.profile?.pupil_id || 0)
);

const periodsQuery = usePeriodsQuery({}, { staleTime: 5 * 60 * 1000 });

const parsedPeriods = computed(() =>
  parsePeriodsLegacy(
    Array.isArray(periodsQuery.data.value) ? periodsQuery.data.value : []
  )
);
const monthItems = computed(() =>
  (parsedPeriods.value?.months || []).map((month) => {
    const date = new Date(month.year, month.month, 1);
    return {
      title: capitalize(date.toLocaleDateString(resolveLocale(), { month: "long" })),
      subtitle: "",
      month,
      year: month.year,
      start: date,
    };
  })
);

const selectedMonthValue = computed(() => selectedMonth.value?.month || null);
const monthStart = computed(() => {
  if (!selectedMonthValue.value) return null;
  return new Date(selectedMonthValue.value.year, selectedMonthValue.value.month, 1);
});
const monthEnd = computed(() => {
  if (!monthStart.value) return null;
  const d = new Date(monthStart.value);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
});

const monthStartTsSec = computed(() =>
  toUtcDaySeconds(monthStart.value)
);
const monthEndTsSec = computed(() =>
  monthEnd.value ? toUtcDaySeconds(monthEnd.value) + 24 * 60 * 60 - 1 : 0
);

const plansQuery = useCalendarPlansQuery(monthStartTsSec, monthEndTsSec, groupId, {
  placeholderData: (previousData) => previousData,
  retry: false,
});
const marksQuery = useCalendarMarksQuery(monthStartTsSec, monthEndTsSec, pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false,
});
const holidaysQuery = useCalendarHolidaysQuery(monthStartTsSec, monthEndTsSec, {
  placeholderData: (previousData) => previousData,
  retry: false,
});

const rawPlans = computed(() =>
  Array.isArray(plansQuery.data.value) ? plansQuery.data.value : []
);
const rawMarks = computed(() =>
  Array.isArray(marksQuery.data.value) ? marksQuery.data.value : []
);
const rawCalendarHolidays = computed(() =>
  Array.isArray(holidaysQuery.data.value) ? holidaysQuery.data.value : []
);

const monthDaySet = computed(
  () => new Set((monthGridBase.value.days || []).map((day) => toDateKey(day.date)))
);

const acceptedPlans = computed(() =>
  rawPlans.value.filter((plan) => {
    const key = toDateKey(getPlanDate(plan));
    if (!monthDaySet.value.has(key)) return false;
    return isDisplayablePlan(plan);
  })
);

const subjectIds = computed(() => {
  const ids = new Set();
  acceptedPlans.value.forEach((plan) => {
    const id = getPlanSubjectId(plan);
    if (id > 0) ids.add(id);
  });
  return [...ids];
});

const subjectsQuery = useCalendarSubjectsByIdsQuery(subjectIds, {
  placeholderData: (previousData) => previousData,
  retry: false,
});

const subjectsMap = computed(() => {
  const map = new Map();
  const rows = Array.isArray(subjectsQuery.data.value) ? subjectsQuery.data.value : [];
  rows.forEach((item) => {
    const id = Number(item?.id || item?._data?.id || 0);
    if (!id) return;
    map.set(id, item?.name || item?._data?.name || "");
  });
  return map;
});

const testDaySet = computed(() => {
  const set = new Set();
  acceptedPlans.value.forEach((plan) => {
    const lessonView = Number(plan?.lesson_view || plan?._data?.lesson_view || 0);
    if (lessonView === 21 || lessonView === 22 || lessonView === 33) {
      set.add(toDateKey(getPlanDate(plan)));
    }
  });
  return set;
});

const absentDaySet = computed(() => {
  const set = new Set();
  rawMarks.value.forEach((mark) => {
    const value = String(mark?.mark_value ?? mark?._data?.mark_value ?? "").trim();
    const absent = mark?.absent === true || ["Н", "H", "Ж"].includes(value);
    if (!absent) return;
    set.add(toDateKey(mark?.date || mark?._data?.date));
  });
  return set;
});

const calendarHolidaySet = computed(() => {
  const set = new Set();
  rawCalendarHolidays.value.forEach((item) => {
    const date = item?.date || item?._data?.date;
    if (!date) return;
    set.add(toDateKey(date));
  });
  return set;
});

const monthGridBase = computed(() =>
  buildMonthGrid(monthStart.value, monthEnd.value, parsedPeriods.value)
);
const monthGrid = computed(() => {
  const clonedWeeks = monthGridBase.value.weeks.map((week) => ({
    ...week,
    days: week.days.map((day) => ({
      ...day,
      class: { ...day.class },
    })),
  }));

  clonedWeeks.forEach((week) => {
    week.days.forEach((day) => {
      const key = toDateKey(day.date);
      if (testDaySet.value.has(key)) day.class.test = true;
      if (absentDaySet.value.has(key)) day.class.absent = true;
      if (calendarHolidaySet.value.has(key)) day.holiday = true;
    });
  });

  return {
    weeks: clonedWeeks,
    days: clonedWeeks.flatMap((week) => week.days),
  };
});

const holidayRangeText = computed(() => {
  const holidays = Array.isArray(parsedPeriods.value?.holidays)
    ? parsedPeriods.value.holidays
    : [];
  if (!monthStart.value || !monthEnd.value) return "";
  const monthStartTs = monthStart.value.getTime();
  const monthEndTs = monthEnd.value.getTime();

  for (let i = 0; i < holidays.length; i += 1) {
    const start = toLocalDayTimestamp(holidays[i].start);
    const end = toLocalDayTimestamp(holidays[i].end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    const overlapStart = Math.max(start, monthStartTs);
    const overlapEnd = Math.min(end, monthEndTs);
    const intersects = overlapStart <= overlapEnd;

    if (!intersects) continue;

    const from = formatLongDate(addDays(new Date(start), 1));
    const to = formatLongDate(addDays(new Date(end), -1));
    return `${from} - ${to}`;
  }
  return "";
});

const groupedPlans = computed(() => {
  const grouped = new Map();

  const sorted = [...acceptedPlans.value].sort(
    (a, b) => new Date(getPlanDate(a) || 0).getTime() - new Date(getPlanDate(b) || 0).getTime()
  );
  sorted.forEach((plan) => {
    const date = String(getPlanDate(plan) || "");
    if (!date) return;
    if (!grouped.has(date)) {
      grouped.set(date, {
        date,
        subjects: [],
      });
    }
    const targetDate = grouped.get(date);
    const subjectId = getPlanSubjectId(plan);
    const existingSubject = targetDate.subjects.find(
      (subject) => subject.id === subjectId
    );

    if (existingSubject) {
      existingSubject.item.push(normalizeCalendarPlan(plan));
      return;
    }

    targetDate.subjects.push({
      id: subjectId,
      name: subjectsMap.value.get(subjectId) || "",
      item: [normalizeCalendarPlan(plan)],
    });
  });

  return [...grouped.values()];
});

const isLoading = computed(
  () =>
    periodsQuery.isFetching.value ||
    plansQuery.isFetching.value ||
    marksQuery.isFetching.value ||
    holidaysQuery.isFetching.value ||
    subjectsQuery.isFetching.value
);
const calendarPageErrors = computed(() =>
  [periodsQuery, plansQuery, marksQuery, holidaysQuery, subjectsQuery]
    .filter((query) => query.isError?.value)
    .map((query) => query.error?.value)
);

watch(
  () => isLoading.value,
  (value) => {
    loadingStore.set("calendar-page", value);
  },
  { immediate: true }
);

watch(
  () => monthItems.value,
  (items) => {
    if (!items.length) return;

    const now = new Date();
    const current = items.find(
      (item) =>
        item.month.month === now.getMonth() && item.month.year === now.getFullYear()
    );
    selectedMonth.value = current || items[0];
  },
  { immediate: true }
);

onMounted(() => {
  topbarStore.setTitle(t("calendar"));
  topbarStore.addButton({
    type: "right",
    icon: "loop",
    action: () => {
      periodsQuery.refetch();
      plansQuery.refetch();
      marksQuery.refetch();
      holidaysQuery.refetch();
      subjectsQuery.refetch();
    },
  });

  nowTimer = setInterval(() => {
    nowTs.value = Date.now();
  }, 60 * 1000);
});

onBeforeUnmount(() => {
  loadingStore.hide("calendar-page");
  if (nowTimer) clearInterval(nowTimer);
});

function onMonthChange(value) {
  selectedMonth.value = value;
}

function getTasks(plan) {
  const list = plan?._data?.ltasks || plan?.ltasks || [];
  return Array.isArray(list) ? list : [];
}

function getPlanDate(plan) {
  return plan?.date || plan?._data?.date || plan?.lesson_date || plan?._data?.lesson_date || "";
}

function getPlanSubjectId(plan) {
  return Number(
    plan?.subject_id ||
      plan?._data?.subject_id ||
      plan?.s_id ||
      plan?._data?.s_id ||
      plan?.subject?.id ||
      plan?._subject?.id ||
      0
  );
}

function getPlanTheme(plan) {
  return String(plan?.theme_name || plan?._data?.theme_name || "").trim();
}

function getPlanHometask(plan) {
  return String(plan?.hometask || plan?._data?.hometask || "").trim();
}

function isDisplayablePlan(plan) {
  return Boolean(getPlanTheme(plan) || getPlanHometask(plan) || getTasks(plan).length);
}

function normalizeCalendarPlan(plan) {
  return {
    ...plan,
    id: plan?.id || plan?._id || plan?._data?.id || 0,
    date: getPlanDate(plan),
    subject_id: getPlanSubjectId(plan),
    theme_name: getPlanTheme(plan),
    hometask: getPlanHometask(plan),
    ltasks: getTasks(plan)
  };
}

function getProgressInfo(task) {
  const endDate = task?.end_date ? new Date(task.end_date) : null;
  if (!endDate || Number.isNaN(endDate.getTime())) {
    return { kind: "none", text: "" };
  }

  const diffMs = endDate.getTime() - nowTs.value;
  if (diffMs <= 0) {
    return { kind: "ended", text: t("timeEndPassTask") };
  }

  const dayMs = 24 * 60 * 60 * 1000;
  if (diffMs < dayMs) {
    const totalMinutes = Math.floor(diffMs / (60 * 1000));
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    return { kind: "timer", text: t("timeToSendTask"), time: `${hours}:${minutes}` };
  }

  return {
    kind: "date",
    text: t("sendTask"),
    date: formatDayMonth(endDate),
  };
}

function navigate(day) {
  router.push(`/schedule?day=${day.ts}`);
}

function navigateMaterial(plan) {
  const firstSubject = plan?.subjects?.[0];
  const firstPlan = firstSubject?.item?.[0];
  const lessonId = firstPlan?.id || firstPlan?._id || "";
  const lessonName = firstSubject?.name || "";
  router.push(
    `/all-tasks?lessonId=${lessonId}&lessonName=${encodeURIComponent(lessonName)}`
  );
}

function getDayType(day) {
  if (day.today) return "today";
  if (day.holiday) return "holiday";
  return "";
}

function formatPlanDate(date) {
  const d = toDateObject(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

function resolveLocale() {
  return localStorage.getItem("translation") === "kz" ? "kk-KZ" : "ru-RU";
}

function capitalize(value) {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildMonthGrid(startDate, endDate, parsed) {
  if (
    !startDate ||
    !endDate ||
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime())
  ) {
    return { weeks: [], days: [] };
  }

  const parsedDays = Array.isArray(parsed?.days) ? parsed.days : [];
  const holidays = Array.isArray(parsed?.holidays) ? parsed.holidays : [];

  const startOfWeek = getMonday(startDate);
  const endOfWeek = addDays(getMonday(endDate), 6);
  const weeks = [];
  const allDays = [];

  let week = { days: [] };
  for (
    let cursor = new Date(startOfWeek);
    cursor <= endOfWeek;
    cursor = addDays(cursor, 1)
  ) {
    const dayTs = cursor.getTime();
    const parsedWorkDay = parsedDays.find((day) => day.date === dayTs);

    const day = {
      date: new Date(cursor),
      ts: dayTs,
      day: cursor.getDate(),
      today: toPeriodDateKey(cursor) === toPeriodDateKey(Date.now()),
      holiday: isPeriodHoliday(holidays, dayTs),
      class: {
        work: Boolean(parsedWorkDay),
        out: cursor < startDate || cursor > endDate,
        rest: cursor.getDay() === 0,
        absent: false,
        test: false,
      },
    };

    week.days.push(day);
    allDays.push(day);

    if (week.days.length === 7) {
      weeks.push(week);
      week = { days: [] };
    }
  }

  return {
    weeks,
    days: allDays,
  };
}

function isPeriodHoliday(holidays, dayTs) {
  for (let i = 0; i < holidays.length; i += 1) {
    const start = toLocalDayTimestamp(holidays[i].start);
    const end = toLocalDayTimestamp(holidays[i].end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    if (dayTs > start && dayTs < end) return true;
  }
  return false;
}

function toDateKey(value) {
  if (!value) return "";
  const date = toDateObject(value);
  if (!date || Number.isNaN(date.getTime())) return "";
  return toPeriodDateKey(date);
}

function toDateObject(value) {
  if (typeof value === "number") {
    const ts = value > 1e12 ? value : value * 1000;
    return new Date(ts);
  }
  return new Date(value);
}

function toLocalDayTimestamp(value) {
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      return new Date(year, month - 1, day).getTime();
    }
  }

  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d;
}

function addDays(date, count) {
  const d = new Date(date);
  d.setDate(d.getDate() + count);
  return d;
}

function formatLongDate(date) {
  return date.toLocaleDateString(resolveLocale(), { day: "numeric", month: "long" });
}

function formatDayMonth(date) {
  return date.toLocaleDateString(resolveLocale(), { day: "numeric", month: "long" });
}
</script>

<style scoped lang="scss">
.starIcon {
  width: 16px;
  font-size: 12px !important;
  margin-right: 6px;
  margin-left: 2px;
}

.ml-2 {
  margin-left: 8px;
}

.text-pink {
  color: #ff5d8a !important;
}

.infoCalendar {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 14px;

  .infoList {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    min-width: 150px;

    .circuleFont {
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      margin-right: 8px;
    }
  }

  .circule {
    border-radius: 50%;
    font-size: 8px;
    height: 8px !important;
  }

  .todayInfo {
    background: #d7f0dd;

    .circule {
      color: #11902f !important;
    }
  }

  .holidayInfo {
    background: #ffdfe8;

    .circule {
      color: #ff5d8a;
    }
  }

  .examCalendar {
    background: #dfccff;

    .circule {
      color: #633e97;
    }
  }

  .dayoff {
    background: #d5f3ff;

    .circule {
      color: #056dcd;
    }
  }
}

.holidays {
  border: 1px solid #1890ff;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
}

.holidaysTitle {
  background: #1890ff;
  border-bottom: 1px solid #1890ff;
  font-family: Roboto, sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #fff;
  padding: 10px 12px;
  display: flex;
}

.holidaysInfo {
  background: #e6f7ff;
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000;
  padding: 8px 12px;
}

.emptyCalendarPlans {
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.54);

  @media screen and (max-width: 950px) {
    display: none;
  }
}

.task__lens {
  position: absolute;
  left: 16px;
  color: #fbad2f;
  margin-top: 18px;
  font-size: 7px;
  background: #fff;
}

.bottom__line {
  position: absolute;
  left: 19px;
  border-bottom: 1px solid #e0e0e0;
  width: 20px;
  margin-top: 26px;
}

.left__line {
  position: absolute;
  left: 19px;
  border-left: 1px solid #e0e0e0;
  height: 100%;
  top: 0;
  bottom: 0;
}

.end__date {
  font-size: 14px;
  color: #000;
}

.task__head {
  margin-top: 11px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding: 8px;
  margin-right: 16px;

  .task__name {
    color: #000;
  }

  .trTask {
    color: #36b453;
  }
}

.subject {
  display: flex;
  flex-direction: column;
  width: 100%;

  .theme__name {
    font-size: 14px;
    color: #8b8b8d;
  }

  .subject__name {
    height: 24px;
    color: #151b1e;
    font-size: 16px;
    font-family: Roboto, sans-serif;
    font-weight: 500;
  }
}

.lens__head {
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  background: #fff;
  z-index: 2;
}

.lens__body {
  width: 8px;
  height: 8px;
  font-size: 8px;
  z-index: 2;
  color: #4db7fe !important;
}

.today {
  background-color: #d7f0dd !important;
  color: #11902f !important;
}

.holiday {
  background-color: #e6f7ff !important;
}

.week__card__body {
  display: flex;

  @media screen and (max-width: 1300px) {
    flex-direction: column;
  }

  @media screen and (min-width: 1300px) {
    flex-direction: row;
    justify-content: flex-end;
    max-height: 664px;
  }

  @media screen and (min-width: 950px) and (max-width: 1299px) {
    flex-direction: row;
    justify-content: flex-end;
    max-height: 720px;
  }
}

.week__card {
  box-shadow: none !important;
  border: none !important;
  max-width: 1250px;

  @media screen and (max-width: 950px) {
    padding: 0 16px;
  }

  @media screen and (min-width: 950px) {
    padding: 20px 30px 0 !important;
    margin: 0 16px;
  }
}

.month {
  border: 1px solid #e0e0e0;

  @media screen and (min-width: 950px) {
    width: 100%;
  }

  .week {
    white-space: nowrap;
    text-align: center;
    box-shadow: none !important;
    display: flex;
    justify-content: center;

    @media screen and (min-width: 1300px) {
      min-height: 100px;
    }

    &:last-child .day {
      border-bottom: none !important;
    }

    .day {
      display: flex;
      border-right: 1px dashed #bdbdbd !important;
      border-bottom: 1px dashed #bdbdbd !important;
      align-items: center;
      justify-content: center;
      width: 18%;
      font-size: 16px;
      font-weight: 700;
      background-color: #fff;
      vertical-align: top;
      min-height: 47px;
      border-collapse: collapse;
      text-transform: uppercase;

      &:last-child {
        border-right: 0 !important;
      }

      .circle {
        position: relative;
        width: 34px;
        height: 34px;
        margin: 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.8s, color 0.8s;
        cursor: pointer;
        line-height: 1;

        .starDay {
          color: #fbad2f !important;
        }

        > span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        &.out {
          color: #d0d0d0;
          cursor: default;
        }

        &.rest:not(.out) {
          color: #ff5d8a !important;
        }

        &.absent {
          color: #633e97 !important;
          background-color: #dfccff;
        }

        &.test i {
          position: absolute;
          right: -10px;
          top: -4px;
          transform: translate(-50%, 0);
          font-size: 10px;
        }
      }
    }
  }
}

::-webkit-scrollbar {
  width: 4px;
  height: 4px;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #7b7b7c;
  border-radius: 0;
}

::-webkit-scrollbar-thumb:hover {
  background: #4db7fe;
}

::-webkit-scrollbar-track {
  background: #fff;
  border-radius: 0;
  box-shadow: inset 7px 10px 12px #f0f0f0;
}

.plans {
  @media screen and (max-width: 950px) {
    margin-top: 16px;
  }

  @media screen and (min-width: 950px) {
    min-width: 366px;
    margin-left: 16px;
    border: 1px solid #e0e0e0 !important;
    padding: 16px;
    overflow: auto;
  }

  .plan {
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-bottom: 6px;
    border: 1px solid #e0e0e0;
    border-radius: 5px;

    .date {
      border-bottom: 1px solid #e0e0e0 !important;
      display: flex;
      justify-content: space-between;
      min-width: 40px;
      min-height: 10px;
      text-align: center;
      font-size: 20px;
      color: #000;
      padding: 8px;
      font-style: normal;
      font-weight: 700;
      cursor: pointer;
    }

    .icon {
      color: #bdbdbd;
      font-size: 14px;
      display: flex;
      align-items: center;
      margin-right: 9px;
    }

    .info {
      width: auto;
      overflow: hidden;
      margin-top: 16px;
      margin-right: 8px;
    }
  }
}

@media screen and (min-width: 950px) {
  .plans .plan {
    max-width: 366px;
  }
}

.planBody {
  position: relative;
  padding-bottom: 17px;
}
</style>
